import Vue from 'vue'
import Vuex from 'vuex'
import api from '../api'

// Statically load options arrays
import allLegends from '../options/legends'
import allOrganModes from '../options/organ-modes'
import allRegionModes from '../options/region-modes'
import allStatistics from '../options/statistics'
import allYears from '../options/years'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    /**
     * The list legends used by statistics.
     */
    allLegends: undefined,

    /**
     * The list of all possible Organ Modes.
     */
    allOrganModes: undefined,

    /**
     * The list of all possible Region Modes.
     */
    allRegionModes: undefined,

    /**
     * List of statistics
     * */
    allStatistics: undefined,

    /**
     * The list of all possible Years.
     */
    allYears: undefined,

    /**
     * Each of these flags saves to loading state of resources required by the app
     */
    asyncReady: {
      data: false,
      markers: false,
      opoShapes: false,
      unosShapes: false,
      stateStapes: false,
      statistics: false
    },

    disclaimerVisible: false,

    /**
     * The Google API. Will be loaded asynchronously by the map.
     */
    google: undefined,

    /**
     * The Marker on the Map.
     */
    hoveredMarker: null,

    /**
     * List of Hospitals.
     * Can be used as Markers' source.
     */
    hospitals: [],

    /**
     * Statistics records about Hospitals.
     */
    hospitalRecords: [],

    /**
     * Foe discrete legend, the index of the interval on which
     * the user mouse is hovering.
     */
    legendIntervalHover: undefined,

    /**
     * When user mouse is over a region, highlight statistic value
     * on legend.
     */
    legendHover: undefined,

    /**
     * Selected statistic's minumum and maximum values.
     * Used for building the map legend.
     */
    legendMin: NaN,
    legendMax: NaN,

    /**
     * Whenever markers should be visible or hidden on the map.
     */
    markersVisible: true,

    /**
     * GeoJSON shapes for OPO.
     */
    opoShapes: undefined,

    /**
     * Which overlay to show. Can be 'c' con choropleth, or 'k' for kriging.
     */
    overlayMode: 'c',

    // TODO
    preferredTxCenter: undefined,

    /**
     * The OrganMode selected in QueryPanel.
     */
    queryOrganMode: undefined,

    /**
     * The RegionMode selected in QueryPanel.
     */
    queryRegionMode: undefined,

    /**
     * Id of Statistic selected in QueryPanel.
     */
    queryStatisticId: undefined,

    /**
     * If of Statistic's Children selected in QueryPanel.
     */
    queryStatisticSubId: undefined,

    /**
     * Year selected in QueryPanel.
     */
    queryYear: undefined,

    /**
     * Regions available for selected Statistic.
     * depends on query.statistic
     */
    regions: [],

    // TODO
    role: undefined,

    /**
     * Will be assigned a value when a user clicks on a Hospital
     */
    selectedHospital: undefined,

    /**
     * Will be assigned a value when a user clicks on a TxCenter
     */
    selectedTxCenter: undefined,

    /**
     * Will be assigned a value when a user clicks on a OPO/UNO/State
     */
    selectedRegion: undefined,

    /**
     * The ZIP code entered by user
     */
    selectedZip: undefined,
    selectedZipClosestTxCenters: [],
    // selectedZipClosestTxCentersIds: [],
    selectedZipLatLng: undefined,

    /**
     * GeoJSON shapes for States
     */
    stateShapes: undefined,

    /**
     * List of Transplant Centers.
     * Can be used as Markers' source.
     */
    txCenters: [],

    /**
     * Statistics records about Transplant Centers
     */
    txRecords: [],

    /**
     * GeoJSON shapes for UNOS
     */
    unosShapes: undefined
  },

  getters: {
    allLegends: state => state.allLegends,
    allOrganModes: state => state.allOrganModes,
    allRegionModes: state => state.allRegionModes,
    allStatistics: state => state.allStatistics,
    allYears: state => state.allYears,

    disclaimerVisible: state => state.disclaimerVisible,

    queryOrganMode: state => state.queryOrganMode,
    queryRegionMode: state => state.queryRegionMode,
    queryStatisticId: state => state.queryStatisticId,
    queryStatisticSubId: state => state.queryStatisticSubId,
    queryYear: state => state.queryYear,

    google: state => state.google,

    legendIntervalHover: state => state.legendIntervalHover,
    legendHover: state => state.legendHover,
    legendMin: state => state.legendMin,
    legendMax: state => state.legendMax,

    markersVisible: state => state.markersVisible,

    hoveredMarker: state => state.hoveredMarker,
    hospitals: state => state.hospitals,
    hospitalRecords: state => state.hospitalRecords,
    opoShapes: state => state.opoShapes,
    overlayMode: state => state.overlayMode,
    preferredTxCenter: state => state.preferredTxCenter,
    query: state => state.query,
    regions: state => state.regions,
    role: state => state.role,
    selectedRegion: state => state.selectedRegion,
    selectedHospital: state => state.selectedHospital,
    selectedTxCenter: state => state.selectedTxCenter,
    selectedZip: state => state.selectedZip,
    selectedZipClosestTxCenters: state => state.selectedZipClosestTxCenters,
    selectedZipLatLng: state => state.selectedZipLatLng,
    stateShapes: state => state.stateShapes,
    statistics: state => state.statistics,
    txCenters: state => state.txCenters,
    txRecords: state => state.txRecords,
    unosShapes: state => state.unosShapes,

    /**
     *
     */
    selectedStatistic: (state, getters) => state.allStatistics.find(getters.queryStatisticId, getters.queryStatisticSubId),

    /**
     * Return a combination of queryStatisticId and queryStatisticSubId.
     */
    selectedStatisticId: (state, getters) => getters.selectedStatistic.children.length ? state.queryStatisticSubId : state.queryStatisticId,

    /**
     * Note that since selectedStatistic only exists as getter, we need to use
     * the second parameters (getters).
     */
    markersSource: (state, getters) => getters.selectedStatistic.markers
  },

  actions: {
    loadHospitals ({ commit, state, getters }) {
      let processed = []
      let hospitals = []
      for (let i = 0; i < getters.hospitalRecords.length; i++) {
        let h = state.hospitalRecords[i]
        let name = h.donor_hospital
        if (processed.indexOf(name) < 0) {
          let id = hospitals.length
          hospitals.push({
            id,
            name: name,
            latLng: { lat: h.lat, lng: h.lng },
            zip: h.donor_hospital_zip,
            city: h.city,
            state: h.state
          })
          processed.push(name)
        }
      }
      commit('setHospitals', hospitals)
    },

    loadRecords ({ commit, state, dispatch }) {
      api.getTxRecords(
        (records) => { commit('setTxRecords', records) }
      )
      api.getHospitalRecords(
        (records) => {
          commit('setHospitalRecords', records)
          dispatch('loadHospitals')
        }
      )
    },

    loadOpoShapes ({ commit, state }) {
      api.getOpos(
        (shapes) => { commit('setOpoShapes', shapes) }
      )
    },

    loadStateShapes ({ commit, state }) {
      api.getStates(
        (shapes) => { commit('setStateShapes', shapes) }
      )
    },

    loadQueryPanelData ({ commit, state }) {
      commit('setAllLegends', allLegends)
      commit('setAllOrganModes', allOrganModes)
      commit('setAllRegionModes', allRegionModes)
      commit('setAllStatistics', allStatistics)
      commit('setAllYears', allYears)

      // Set a default query
      let defaultStatistic = allStatistics[0]
      commit('queryStatisticId', defaultStatistic.value)
      commit('queryStatisticSubId', (defaultStatistic.children.length > 0) ? defaultStatistic.children[0] : false)
      commit('queryOrganMode', defaultStatistic.organs[0])
      commit('queryRegionMode', defaultStatistic.regions[0])
      commit('queryYear', allYears[0].value)
    },

    loadTxCenters ({ commit, state }) {
      api.getTxCenters(
        (txCenters) => { commit('setTxCenters', txCenters) }
      )
    },

    loadUnosShapes ({ commit, state }) {
      api.getUnos(
        (shapes) => { commit('setUnosShapes', shapes) }
      )
    }
  },

  mutations: {
    queryOrganMode (state, organMode) {
      state.queryOrganMode = organMode
    },

    queryRegionMode (state, regionMode) {
      state.queryRegionMode = regionMode
    },

    queryStatisticId (state, statisticId) {
      state.queryStatisticId = statisticId
    },

    queryStatisticSubId (state, statisticSubId) {
      state.queryStatisticSubId = statisticSubId
    },

    queryYear (state, year) {
      state.queryYear = year
    },

    hoverMarker (state, marker) {
      state.hoveredMarker = marker
    },

    selectHospital (state, hospitalId) {
      state.selectedHospital = hospitalId
    },

    selectRegion (state, region) {
      state.selectedRegion = region
    },

    selectTxCenter (state, txCenterId) {
      state.selectedTxCenter = txCenterId
    },

    selectZip (state, zip) {
      state.selectedZip = zip
    },

    setAsyncReady (state, what) {

    },

    setDisclaimerVisible (state, visible) {
      localStorage.disclaimerVisible = visible
      state.disclaimerVisible = visible
    },

    setGoogle (state, google) {
      state.google = google
    },

    setHospitals (state, hospitals) {
      state.hospitals = hospitals
    },

    setHospitalRecords (state, records) {
      state.hospitalRecords = records
    },

    setLegendIntervalHover (state, index) {
      state.legendIntervalHover = index
    },

    setLegendHover (state, value) {
      state.legendHover = value
    },

    setLegendMinMax (state, { min, max }) {
      state.legendMin = min
      state.legendMax = max
    },

    setMarkersVisible (state, visible) {
      state.markersVisible = visible
    },

    setOpoShapes (state, shapes) {
      state.opoShapes = shapes
    },

    setOverlayMode (state, mode) {
      state.overlayMode = mode
    },

    setPreferredTxCenter (state, txCenter) {
      state.preferredTxCenter = txCenter
    },

    setRole (state, role) {
      state.role = role
    },

    setStateShapes (state, shapes) {
      state.stateShapes = shapes
    },

    setAllLegends (state, legends) {
      state.allLegends = legends
    },

    setAllOrganModes (state, organModes) {
      state.allOrganModes = organModes
    },

    setAllRegionModes (state, regionModes) {
      state.allRegionModes = regionModes
    },

    setAllStatistics (state, statistics) {
      state.allStatistics = statistics
    },

    setAllYears (state, years) {
      state.allYears = years
    },

    setSelectedZipClosestTxCenters (state, txCenters) {
      state.selectedZipClosestTxCenters = txCenters
    },

    // setSelectedZipClosestTxCentersIds (state, ids) {
    //   state.selectedZipClosestTxCentersIds = ids
    // },

    setSelectedZipLatLng (state, latLng) {
      state.selectedZipLatLng = latLng
    },

    setTxCenters (state, txCenters) {
      state.txCenters = txCenters
    },

    setTxRecords (state, records) {
      state.txRecords = records
    },

    setUnosShapes (state, shapes) {
      state.unosShapes = shapes
    }
  }
})



// WEBPACK FOOTER //
// ./src/store/index.js